import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Mail, Trash2, RefreshCw, Users, Copy, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, local, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { PaywallGate } from "@/components/PaywallGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { initials, titleCase, fmtDate } from "@/lib/format";

const ROLES = ["doctor", "nurse", "receptionist", "pharmacist", "lab_scientist", "radiographer", "accountant", "hospital_admin"];

export default function Staff() {
  const qc = useQueryClient();
  const { org } = useAuth();
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState({ invitee_email: "", invitee_name: "", role_slug: "doctor", department: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkRole, setBulkRole] = useState("doctor");
  const [bulkDept, setBulkDept] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [csvInvites, setCsvInvites] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);

  const parseBulk = () => bulkText.split(/[\n,;]+/).map((s) => s.trim()).filter((s) => s.includes("@")).map((email) => ({ invitee_email: email, role_slug: bulkRole, department: bulkDept || undefined }));

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const header = lines[0].toLowerCase();
    const hasHeader = header.includes("email");
    const cols = hasHeader ? header.split(/[,;\t]/).map((c) => c.trim()) : ["email", "role", "department"];
    const ei = cols.indexOf("email"), ri = cols.indexOf("role"), di = cols.indexOf("department");
    return lines.slice(hasHeader ? 1 : 0).map((l) => {
      const p = l.split(/[,;\t]/).map((c) => c.trim());
      const email = p[ei >= 0 ? ei : 0];
      if (!email || !email.includes("@")) return null;
      return { invitee_email: email, role_slug: (ri >= 0 && p[ri]) ? p[ri].toLowerCase().replace(/\s+/g, "_") : bulkRole, department: (di >= 0 ? p[di] : "") || bulkDept || undefined };
    }).filter(Boolean);
  };

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const inv = parseCSV(await file.text());
    setCsvInvites(inv);
    setBulkText(inv.map((i) => i.invitee_email).join("\n"));
    toast.success(`${inv.length} rows parsed from ${file.name}`);
  };

  const effectiveInvites = () => (csvInvites && csvInvites.length ? csvInvites : parseBulk());

  const emailInvites = (created) => {
    if (!sendEmails || !created?.length) return;
    local.post("/invitations/email", { public_base: window.location.origin, invites: created.map((c) => ({ email: c.email, invite_link: c.invite_link, usic: c.usic || "" })) }).catch(() => {});
  };

  const { data: staff = [], isLoading } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data || [] });
  const { data: invites = [] } = useQuery({ queryKey: ["invitations"], queryFn: async () => (await api.get("/invitations")).data || [] });

  const inviteMut = useMutation({
    mutationFn: async () => (await api.post("/invitations", invite)).data,
    onSuccess: (d) => { if (d?.invite_link) emailInvites([{ email: d.invitee_email, invite_link: d.invite_link, usic: d.usic }]); toast.success(sendEmails ? "Invitation emailed" : "Invitation created"); qc.invalidateQueries({ queryKey: ["invitations"] }); setOpen(false); setInvite({ invitee_email: "", invitee_name: "", role_slug: "doctor", department: "" }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const bulkMut = useMutation({
    mutationFn: async () => (await api.post("/invitations/bulk", { invites: effectiveInvites() })).data,
    onSuccess: (d) => { emailInvites(d.created); setBulkResult(d); toast.success(`${d.created_count} invited${d.skipped_count ? `, ${d.skipped_count} skipped` : ""}${sendEmails ? " · emails sent" : ""}`); qc.invalidateQueries({ queryKey: ["invitations"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revokeMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/invitations/${id}`)).data,
    onSuccess: () => { toast.success("Invitation revoked"); qc.invalidateQueries({ queryKey: ["invitations"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const resendMut = useMutation({
    mutationFn: async (id) => (await api.post(`/invitations/${id}/resend`)).data,
    onSuccess: () => toast.success("Invitation resent"),
    onError: (e) => toast.error(errMsg(e)),
  });
  const statusMut = useMutation({
    mutationFn: async ({ uid, status }) => (await api.put(`/staff/${uid}/status`, { status })).data,
    onSuccess: () => { toast.success("Staff updated"); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const staffCols = [
    { key: "full_name", label: "Name", render: (r) => (
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">{initials(r.full_name)}</span>
        <div><p className="font-medium text-slate-800">{r.full_name}</p><p className="text-xs text-slate-400">{r.email}</p></div>
      </div>
    )},
    { key: "role", label: "Role", render: (r) => titleCase(r.role) },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status || "active"} /> },
    { key: "actions", label: "", render: (r) => (
      <Button size="sm" variant="outline" data-testid={`toggle-staff-${r.user_id}`} onClick={() => statusMut.mutate({ uid: r.user_id, status: (r.status === "suspended" ? "active" : "suspended") })}>
        {r.status === "suspended" ? "Activate" : "Suspend"}
      </Button>
    )},
  ];

  const inviteCols = [
    { key: "invitee_email", label: "Email" },
    { key: "role_slug", label: "Role", render: (r) => titleCase(r.role_slug || r.role) },
    { key: "status", label: "Status", type: "badge" },
    { key: "created_at", label: "Sent", render: (r) => fmtDate(r.created_at) },
    { key: "actions", label: "", render: (r) => (
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" data-testid={`resend-${r.id}`} onClick={() => resendMut.mutate(r.id)}><RefreshCw className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`revoke-${r.id}`} onClick={() => revokeMut.mutate(r.id)}><Trash2 className="h-4 w-4" /></Button>
      </div>
    )},
  ];

  return (
    <PaywallGate>
      <PageHeader title="Staff & Access" subtitle={`${staff.length} team members`} testId="page-staff">
        <Button data-testid="bulk-invite" variant="outline" className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50" onClick={() => { setBulkResult(null); setBulkText(""); setBulkOpen(true); }}><Users className="h-4 w-4" /> Bulk invite</Button>
        <Button data-testid="invite-staff" className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> Invite staff</Button>
      </PageHeader>

      <Tabs defaultValue="team">
        <TabsList>
          <TabsTrigger value="team" data-testid="tab-team">Team ({staff.length})</TabsTrigger>
          <TabsTrigger value="invites" data-testid="tab-invites">Invitations ({invites.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="team"><DataTable columns={staffCols} rows={staff} loading={isLoading} testId="table-staff" /></TabsContent>
        <TabsContent value="invites">
          <DataTable columns={inviteCols} rows={invites} emptyTitle="No pending invitations" emptyDescription="Invite colleagues to join your organisation." emptyAction="Invite staff" onEmptyAction={() => setOpen(true)} testId="table-invites" />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Mail className="h-5 w-5 text-teal-700" /> Invite staff member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="invite-email" type="email" value={invite.invitee_email} onChange={(e) => setInvite({ ...invite, invitee_email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Name (optional)</Label><Input data-testid="invite-name" value={invite.invitee_name} onChange={(e) => setInvite({ ...invite, invitee_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={invite.role_slug} onValueChange={(v) => setInvite({ ...invite, role_slug: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Department (optional)</Label><Input data-testid="invite-dept" value={invite.department} onChange={(e) => setInvite({ ...invite, department: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="invite-send" disabled={!invite.invitee_email || inviteMut.isPending} className="bg-teal-700 hover:bg-teal-800" onClick={() => inviteMut.mutate()}>
              {inviteMut.isPending ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Users className="h-5 w-5 text-teal-700" /> Bulk invite staff</DialogTitle>
            <DialogDescription>Paste emails or upload a CSV to invite multiple staff at once.</DialogDescription>
          </DialogHeader>
          {!bulkResult ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Email addresses</Label>
                <Textarea data-testid="bulk-emails" rows={6} value={bulkText} onChange={(e) => { setBulkText(e.target.value); setCsvInvites(null); }} placeholder={"Paste one email per line…\ndoctor1@hospital.com\nnurse2@hospital.com"} />
                <p className="text-xs text-slate-400">Each invitee gets a sequential staff number (SN-####) and a USIC embedding your OSIC {org?.osic ? `(${org.osic})` : ""}.</p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-700">
                    <Upload className="h-4 w-4" /> Upload CSV
                    <input data-testid="bulk-csv" type="file" accept=".csv,text/csv" className="hidden" onChange={onCsvFile} />
                  </label>
                  <span className="text-xs text-slate-400">CSV columns: email, role, department</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Default role</Label>
                  <Select value={bulkRole} onValueChange={setBulkRole}>
                    <SelectTrigger data-testid="bulk-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Department (optional)</Label><Input data-testid="bulk-dept" value={bulkDept} onChange={(e) => setBulkDept(e.target.value)} /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <span>{effectiveInvites().length} invitee(s) ready</span>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600"><Switch data-testid="bulk-send-emails" checked={sendEmails} onCheckedChange={setSendEmails} /> Email invite links</label>
              </div>
            </div>
          ) : (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="rounded-lg bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">{bulkResult.created_count} invited</span>
                {bulkResult.skipped_count > 0 && <span className="rounded-lg bg-amber-50 px-3 py-1 font-semibold text-amber-700">{bulkResult.skipped_count} skipped (already invited)</span>}
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Staff #</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">USIC</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Link</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {(bulkResult.created || []).map((c, i) => (
                      <tr key={i} data-testid={`bulk-created-${i}`}>
                        <td className="px-3 py-2 text-slate-700">{c.email}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{c.staff_number}</td>
                        <td className="px-3 py-2 font-mono text-teal-700">{c.usic}</td>
                        <td className="px-3 py-2">
                          <button data-testid={`copy-link-${i}`} onClick={() => { navigator.clipboard?.writeText(c.invite_link); toast.success("Invite link copied"); }} className="inline-flex items-center gap-1 text-teal-700 hover:underline"><Copy className="h-3.5 w-3.5" /> Copy</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
            {!bulkResult && <Button data-testid="bulk-send" disabled={effectiveInvites().length === 0 || bulkMut.isPending} className="bg-teal-700 hover:bg-teal-800" onClick={() => bulkMut.mutate()}>{bulkMut.isPending ? "Inviting…" : `Invite ${effectiveInvites().length}`}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PaywallGate>
  );
}
